import { EnemyState, EnemyType, Owner, ProjectileFlags, ProjectileKind } from '../constants';
import { getEnemyArchetype } from '../data/enemies';
import type { GameWorld } from '../world/GameWorld';

type EnemyBehavior = (world: GameWorld, index: number) => void;

function setState(world: GameWorld, index: number, state: EnemyState, timer = 0): void {
  world.enemies.state[index] = state;
  world.enemies.stateTimer[index] = timer;
}

function aimedShot(world: GameWorld, x: number, y: number, speed: number, kind: ProjectileKind, flags = ProjectileFlags.None, angleOffset = 0): void {
  const dx = world.player.x - x;
  const dy = world.player.y - y;
  const lenSq = dx * dx + dy * dy;
  const inv = lenSq > 0.0001 ? 1 / Math.sqrt(lenSq) : 1;
  const base = Math.atan2(dy * inv, dx * inv) + angleOffset;
  const scaled = speed * world.difficultySpec.projectileSpeed;
  world.projectiles.spawn(x, y, Math.cos(base) * scaled, Math.sin(base) * scaled, 2.4, 240, 1, Owner.Enemy, kind, flags, world.nextAttackId());
}

function angleShot(
  world: GameWorld,
  x: number,
  y: number,
  angle: number,
  speed: number,
  kind: ProjectileKind,
  flags = ProjectileFlags.None,
  source = 0,
): void {
  const scaled = speed * world.difficultySpec.projectileSpeed;
  world.projectiles.spawn(x, y, Math.cos(angle) * scaled, Math.sin(angle) * scaled, 2.4, 240, 1, Owner.Enemy, kind, flags, world.nextAttackId(), source);
}

function isInverseCityFlow(world: GameWorld, index: number): boolean {
  const pattern = world.enemies.pattern[index];
  return world.currentStage === 2 && pattern >= 9 && pattern <= 11;
}

function commonSpawn(world: GameWorld, index: number): boolean {
  const pool = world.enemies;
  const state = pool.state[index];
  if (state === EnemyState.Dying) {
    pool.stateTimer[index] -= 1;
    if (pool.stateTimer[index] <= 0) pool.release(index);
    return true;
  }
  if (state === EnemyState.Disabled) {
    pool.stateTimer[index] -= 1;
    pool.x[index] += isInverseCityFlow(world, index) ? 0.45 : -0.45;
    if (pool.stateTimer[index] <= 0) setState(world, index, EnemyState.Navigate, 0);
    return true;
  }
  if (state === EnemyState.Spawn) {
    pool.stateTimer[index] += 1;
    if (pool.stateTimer[index] >= 12) setState(world, index, EnemyState.Enter, 0);
    return true;
  }
  return false;
}

function threadling(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  if (p.state[i] === EnemyState.Enter) {
    p.vx[i] = isInverseCityFlow(world, i) ? 1.2 : -1.2;
    p.aux0[i] = (i * 0.71 + p.pattern[i]) % 6.28318;
    setState(world, i, EnemyState.Navigate, 0);
  }
  p.x[i] += p.vx[i];
  p.y[i] += Math.sin(world.simTick * 0.075 + p.aux0[i]) * 0.58;
  p.attackTimer[i] += 1;
  if (p.attackTimer[i] >= 118 && world.difficulty !== 0) {
    p.attackTimer[i] = 0;
    aimedShot(world, p.x[i], p.y[i], 1.55, ProjectileKind.EnemyOrb);
  }
}

function plateFin(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  if (p.state[i] === EnemyState.Enter) {
    p.vx[i] = isInverseCityFlow(world, i) ? 0.82 : -0.82;
    p.vy[i] = p.y[i] < 108 ? 0.42 : -0.42;
    setState(world, i, EnemyState.Navigate, 0);
  }
  p.x[i] += p.vx[i];
  p.y[i] += p.vy[i];
  if (p.y[i] < 24 || p.y[i] > 192) p.vy[i] = -p.vy[i];
  p.attackTimer[i] += 1;
  if (p.attackTimer[i] >= 105) {
    p.attackTimer[i] = 0;
    aimedShot(world, p.x[i] - 4, p.y[i], 1.8, ProjectileKind.EnemyNeedle);
  }
}

function indexer(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  const state = p.state[i];
  if (state === EnemyState.Enter) {
    p.x[i] -= 0.8;
    if (p.x[i] <= 310) setState(world, i, EnemyState.Aim, 35);
  } else if (state === EnemyState.Aim) {
    p.stateTimer[i] -= 1;
    p.aux0[i] = world.player.y;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Telegraph, Math.round(42 * world.difficultySpec.telegraphScale));
  } else if (state === EnemyState.Telegraph) {
    p.stateTimer[i] -= 1;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Attack, 0);
  } else if (state === EnemyState.Attack) {
    const dx = world.player.x - p.x[i];
    const dy = p.aux0[i] - p.y[i];
    const angle = Math.atan2(dy, dx);
    angleShot(world, p.x[i], p.y[i], angle - 0.12, 1.9, ProjectileKind.EnemyNeedle);
    angleShot(world, p.x[i], p.y[i], angle, 1.9, ProjectileKind.EnemyNeedle);
    angleShot(world, p.x[i], p.y[i], angle + 0.12, 1.9, ProjectileKind.EnemyNeedle);
    setState(world, i, EnemyState.Recover, 80);
  } else if (state === EnemyState.Recover) {
    p.stateTimer[i] -= 1;
    p.x[i] -= 0.35;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Aim, 25);
  }
}

function shadePolyp(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  const state = p.state[i];
  if (state === EnemyState.Enter) {
    setState(world, i, EnemyState.Telegraph, Math.round(70 * world.difficultySpec.telegraphScale));
  } else if (state === EnemyState.Telegraph) {
    p.stateTimer[i] -= 1;
    p.aux0[i] = 1 - p.stateTimer[i] / 70;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Navigate, 0);
  } else {
    p.x[i] -= 0.68;
    p.y[i] += Math.sin(world.simTick * 0.04 + i) * 0.32;
    p.attackTimer[i] += 1;
    if (p.attackTimer[i] >= 126) {
      p.attackTimer[i] = 0;
      for (let n = 0; n < 5; n += 1) angleShot(world, p.x[i], p.y[i], Math.PI + (n - 2) * 0.26, 1.25, ProjectileKind.EnemyOrb);
    }
  }
}

function mirrorWisp(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  if (p.state[i] === EnemyState.Enter) setState(world, i, EnemyState.Navigate, 0);
  p.x[i] -= 0.94;
  p.y[i] += Math.sin(world.simTick * 0.055 + i * 0.7) * 0.52;
  p.aux0[i] += 1;
  if (p.aux0[i] >= 96) {
    p.aux0[i] = 0;
    aimedShot(world, p.x[i], p.y[i], 1.45, ProjectileKind.EnemyOrb);
  }
}

function lockBeetle(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  const state = p.state[i];
  const inverse = isInverseCityFlow(world, i);
  if (state === EnemyState.Enter) {
    p.x[i] += inverse ? 0.65 : -0.65;
    if ((inverse && p.x[i] >= 78) || (!inverse && p.x[i] <= 318)) {
      setState(world, i, EnemyState.Telegraph, Math.round(55 * world.difficultySpec.telegraphScale));
    }
  } else if (state === EnemyState.Telegraph) {
    p.stateTimer[i] -= 1;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Attack, 0);
  } else if (state === EnemyState.Attack) {
    aimedShot(world, p.x[i], p.y[i], 1.35, ProjectileKind.Lock, ProjectileFlags.Homing);
    setState(world, i, EnemyState.Recover, 105);
  } else {
    p.stateTimer[i] -= 1;
    p.x[i] += inverse ? 0.24 : -0.24;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Telegraph, Math.round(48 * world.difficultySpec.telegraphScale));
  }
}

function foldRay(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  const state = p.state[i];
  if (state === EnemyState.Enter) {
    p.x[i] -= 0.7;
    if (p.x[i] <= 342) setState(world, i, EnemyState.Telegraph, Math.round(100 * world.difficultySpec.telegraphScale));
  } else if (state === EnemyState.Telegraph) {
    p.stateTimer[i] -= 1;
    p.aux0[i] = world.player.y;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Attack, 18);
  } else if (state === EnemyState.Attack) {
    if (p.stateTimer[i] === 18) {
      world.projectiles.spawn(0, p.aux0[i], 0, 0, 3, 24, 1, Owner.Enemy, ProjectileKind.Laser, ProjectileFlags.Unblockable | ProjectileFlags.Segment, world.nextAttackId(), 0, 0, 384, p.aux0[i]);
      world.emitSfx(5);
    }
    p.stateTimer[i] -= 1;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Recover, 120);
  } else {
    p.stateTimer[i] -= 1;
    p.x[i] -= 0.18;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Telegraph, Math.round(95 * world.difficultySpec.telegraphScale));
  }
}

function archiveRam(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  const state = p.state[i];
  if (state === EnemyState.Enter) {
    p.x[i] -= 0.55;
    if (p.x[i] <= 322) setState(world, i, EnemyState.Telegraph, Math.round(65 * world.difficultySpec.telegraphScale));
  } else if (state === EnemyState.Telegraph) {
    p.stateTimer[i] -= 1;
    p.aux0[i] = world.player.y;
    p.y[i] += (p.aux0[i] - p.y[i]) * 0.04;
    if (p.stateTimer[i] <= 0) {
      p.vx[i] = -4.6;
      setState(world, i, EnemyState.Attack, 90);
    }
  } else if (state === EnemyState.Attack) {
    p.x[i] += p.vx[i];
    p.stateTimer[i] -= 1;
    if (p.x[i] < 12 || p.stateTimer[i] <= 0) {
      p.x[i] = 410;
      p.vx[i] = 0;
      setState(world, i, EnemyState.Recover, 80);
    }
  } else {
    p.stateTimer[i] -= 1;
    p.x[i] -= 0.25;
    if (p.stateTimer[i] <= 0) setState(world, i, EnemyState.Telegraph, Math.round(55 * world.difficultySpec.telegraphScale));
  }
}

function choirEngine(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  if (p.state[i] === EnemyState.Enter) {
    p.x[i] -= 0.55;
    if (p.x[i] <= 320) setState(world, i, EnemyState.Navigate, 0);
    return;
  }
  p.y[i] += Math.sin(world.simTick * 0.025 + i) * 0.28;
  p.x[i] -= 0.12;
  p.attackTimer[i] += 1;
  if (p.attackTimer[i] >= 135) {
    p.attackTimer[i] = 0;
    const count = world.difficulty === 2 ? 12 : 8;
    for (let n = 0; n < count; n += 1) angleShot(world, p.x[i], p.y[i], (n / count) * Math.PI * 2, 1.28, ProjectileKind.EnemyOrb);
    world.emitSfx(4);
  }
}

function prismEater(world: GameWorld, i: number): void {
  const p = world.enemies;
  if (commonSpawn(world, i)) return;
  if (p.state[i] === EnemyState.Enter) {
    p.x[i] -= 0.6;
    if (p.x[i] <= 315) setState(world, i, EnemyState.Navigate, 0);
    return;
  }
  let target = 0;
  let best = 1e9;
  for (let n = 0; n < 3; n += 1) {
    const dx = world.nodes.x[n] - p.x[i];
    const dy = world.nodes.y[n] - p.y[i];
    const d = dx * dx + dy * dy;
    if (d < best) { best = d; target = n; }
  }
  p.y[i] += (world.nodes.y[target] - p.y[i]) * 0.018;
  p.x[i] -= 0.15;
  p.attackTimer[i] += 1;
  if (p.attackTimer[i] >= 92) {
    p.attackTimer[i] = 0;
    const dx = world.nodes.x[target] - p.x[i];
    const dy = world.nodes.y[target] - p.y[i];
    const angle = Math.atan2(dy, dx);
    angleShot(world, p.x[i], p.y[i], angle, 1.55, ProjectileKind.EnemyNeedle, ProjectileFlags.Homing, target + 1);
    if (world.difficulty === 2) {
      angleShot(world, p.x[i], p.y[i], angle - 0.2, 1.45, ProjectileKind.EnemyNeedle);
      angleShot(world, p.x[i], p.y[i], angle + 0.2, 1.45, ProjectileKind.EnemyNeedle);
    }
  }
}

const BEHAVIORS: readonly EnemyBehavior[] = Object.freeze([
  threadling,
  plateFin,
  indexer,
  shadePolyp,
  mirrorWisp,
  lockBeetle,
  foldRay,
  archiveRam,
  choirEngine,
  prismEater,
]);

export class EnemySystem {
  update(world: GameWorld): void {
    // HOTPATH-BEGIN
    const pool = world.enemies;
    const active = pool.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0) continue;
      pool.prevX[i] = pool.x[i];
      pool.prevY[i] = pool.y[i];
      const behavior = BEHAVIORS[pool.type[i]] ?? threadling;
      behavior(world, i);
      if (active[i] === 0) continue;
      const exited = isInverseCityFlow(world, i) ? pool.x[i] > 464 : pool.x[i] < -80;
      if (exited) pool.release(i);
    }
    // HOTPATH-END
  }

  spawn(world: GameWorld, type: EnemyType, x: number, y: number, pattern: number, variant: number): number {
    const archetype = getEnemyArchetype(type);
    const hpScale = world.difficulty === 0 ? 0.88 : world.difficulty === 2 ? 1.14 : 1;
    return world.enemies.spawn(type, x, y, archetype.hp * hpScale, archetype.radius, pattern, variant, archetype.score, archetype.lux);
  }
}
